import { isCaptureChannel, type CaptureChannel } from "@/lib/toolNames";
import { hasAnalyticsConsent } from "@/lib/consent";

const CONSUMED_COOKIE = "rl_channel_consumed";

/**
 * Cookie de primera parte `rl_channel` con la última vía de captación usada.
 * Compartida entre www y panel (Domain=.retaillemon.com) y caducidad 24 h, para
 * atribuir el alta aunque se complete en otro dominio (email, Google, One Tap).
 */
export const CHANNEL_COOKIE = "rl_channel";
const MAX_AGE = 24 * 60 * 60;

const domainAttr = (): string => {
  if (typeof window === "undefined") return "";
  return /(^|\.)retaillemon\.com$/i.test(window.location.hostname) ? "; Domain=.retaillemon.com" : "";
};

/** Hay sesión iniciada (lectura síncrona del almacenamiento de sesión). */
export function hasStoredSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) ?? "";
      if (k.startsWith("sb-") && k.endsWith("-auth-token") && localStorage.getItem(k)) return true;
    }
  } catch { /* ignore */ }
  return false;
}

export function readChannel(): CaptureChannel | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)rl_channel=([^;]+)/);
  const v = m?.[1] ? decodeURIComponent(m[1]) : null;
  return isCaptureChannel(v) ? v : null;
}

/** Guarda la vía (la última manda). Nunca para quien ya tiene sesión: no sería un alta nueva. */
export function rememberChannel(channel: CaptureChannel | null) {
  if (!channel || typeof document === "undefined") return;
  if (hasStoredSession() || !hasAnalyticsConsent()) return;
  document.cookie = `${CHANNEL_COOKIE}=${encodeURIComponent(channel)}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${domainAttr()}`;
}

const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
};

/** Vía ya consumida en el alta (cookie compartida www/panel, 1 h) para waitlist_source. */
export function clearConsumedChannel() {
  if (typeof document === "undefined") return;
  document.cookie = `${CONSUMED_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${domainAttr()}`;
  document.cookie = `${CONSUMED_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export function clearChannel() {
  if (typeof document === "undefined") return;
  document.cookie = `${CHANNEL_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${domainAttr()}`;
  document.cookie = `${CHANNEL_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/** Valor de waitlist_source: la vía si existe; si no, el valor actual. */
export const waitlistSourceFor = (fallback: string): string => {
  const c = readChannel();
  if (c) return c;
  const v = readCookie(CONSUMED_COOKIE);
  return isCaptureChannel(v) ? v : fallback;
};

/** Alta nueva: cuenta creada hace menos de 24 h (cubre la confirmación por email). */
export const isNewAccount = (createdAt: string | null | undefined, now = Date.now()): boolean =>
  !!createdAt && now - new Date(createdAt).getTime() < MAX_AGE * 1000;

/** Destino del evento signup_completed para cada vía. */
export function signupTargetFor(channel: CaptureChannel):
  | { kind: "landing"; landingSlug: string }
  | { kind: "tool"; toolKey: string; variant?: string } {
  switch (channel) {
    case "lp-instagram":
    case "test-supervivencia":
      return { kind: "landing", landingSlug: channel };
    case "calculadora":
      return { kind: "tool", toolKey: "calculadora" };
    case "diagnostico_inquilino":
      return { kind: "tool", toolKey: "diagnostico_locales", variant: "inquilino" };
    case "diagnostico_propietario":
      return { kind: "tool", toolKey: "diagnostico_locales", variant: "propietario" };
    case "detector_express":
      return { kind: "tool", toolKey: "detector_fugas", variant: "express" };
    case "detector_completo":
      return { kind: "tool", toolKey: "detector_fugas_completo", variant: "completo" };
  }
}

let consumed = false;
/**
 * Tras el alta: si hay vía y la cuenta es nueva, registra signup_completed una sola vez
 * y borra la cookie. Con cuenta antigua solo borra la cookie.
 */
export async function consumeChannelSignup(user: { created_at?: string | null } | null | undefined): Promise<boolean> {
  const channel = readChannel();
  if (!channel || !user || consumed) return false;
  if (!isNewAccount(user.created_at)) { clearChannel(); return false; }
  consumed = true;
  // Solo cuenta nueva: se conserva 1 h entre www y panel para waitlist_source.
  if (typeof document !== "undefined") document.cookie = `${CONSUMED_COOKIE}=${encodeURIComponent(channel)}; Max-Age=3600; Path=/; SameSite=Lax${domainAttr()}`;
  clearChannel();
  const target = signupTargetFor(channel);
  try {
    if (target.kind === "landing") {
      const { trackLandingEvent } = await import("@/lib/landingAnalytics");
      await trackLandingEvent({ landingSlug: target.landingSlug, eventType: "signup_completed", metadata: { channel } }, { once: true, skipGa: true });
    } else {
      const { trackPublicEvent } = await import("@/lib/publicActivity");
      await trackPublicEvent({ eventType: "signup_completed", toolKey: target.toolKey, metadata: { channel, ...(target.variant ? { tool_variant: target.variant } : {}) } }, { once: true });
    }
  } catch { /* noop */ }
  return true;
}

/** Solo para tests. */
export const __resetChannelForTests = () => { consumed = false; };
